<template>
  <div class="position-relative w-100">
    <div v-if="!readonly && normalizedWorkers.length > 0" class="d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center">
        <select v-model="currentMonth" class="custom-select-filters mr-2">
          <option v-for="month in availableMonths" :key="month.index" :value="month.index">{{ month.name }}</option>
        </select>

        <select v-model="currentYear" class="custom-select-filters">
          <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>
      <button
        id="planning-btn"
        type="button"
        class="custom-btn-secondary"
        :class="{ 'is-btn-flashing': isBtnFlashing }"
        @click="startPlanning()"
      >
        <v-icon name="buttons/calendar" :size="16" class="mr-1"/>
        Запланировать
      </button>
    </div>

    <div
      ref="scrollContainer"
      class="timesheet-container"
      :class="{
        'is-planning': planningMode,
        'is-flashing': isFlashing,
        'scroll-locked': scrollLocked
      }"
    >
      <div v-if="normalizedWorkers.length > 0">
        <div class="timesheet-row timesheet-header">
          <div class="worker-col-header text-muted">Исполнители</div>
          <div
            v-for="(day, dIndex) in daysInCurrentMonth"
            :key="day.dateString"
            :id="day.dayNumber === 5 ? 'target-cell-5' : null"
            class="day-header-cell"
            :class="{
              'is-weekend': day.isWeekend,
              'is-col-hovered': hoveredDayIndex === dIndex,
              'is-clickable': !readonly && !planningMode,
            }"
            @click="toggleColumn(day.dateString)"
          >
            <span class="day-name">{{ day.dayName }}</span>
            <span class="day-number">{{ day.dayNumber }}</span>
          </div>
        </div>

        <div class="timesheet-body" @mouseleave="hoveredDayIndex = null">
          <div
            v-for="(worker, wIndex) in normalizedWorkers"
            :key="worker.id"
            class="timesheet-row"
          >
            <div class="worker-col-name text-truncate" :title="worker.name">
              {{ worker.name }}
            </div>

            <div
              v-for="(day, dIndex) in daysInCurrentMonth"
              :key="day.dateString"
              :id="day.dayNumber === 1 ? 'target-cell-1' : null"
              class="day-cell"
              :class="{
                'is-selected': isSelected(worker.id, day.dateString),
                'is-readonly': readonly,
                'is-disabled': planningMode && !isSelected(worker.id, day.dateString),
                'is-col-hovered': hoveredDayIndex === dIndex,
                'is-dragged': isCellInDragArea(wIndex, dIndex),
              }"
              @mouseenter="onMouseEnter(wIndex, dIndex, worker.id, day.dateString)"
              @mousedown.prevent="onMouseDown(wIndex, dIndex, worker.id, day.dateString)"
            />
          </div>
        </div>
      </div>

      <div v-else-if="!readonly" class="empty-state-container mt-5">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>Нет исполнителей для отображения.</h5>
        <p>Сначала добавьте исполнителей, прежде чем перейти к табелю</p>
      </div>
      <div v-else class="p-3 text-muted text-center w-100">
        <v-icon name="navbar/empty_table" :size="90"/>
        Нет исполнителей для отображения
      </div>
    </div>

    <b-modal
      v-model="isPlaningModalOpen"
      title="Планирование работ"
      size="md"
      centered
      modal-class="custom-modal"
      @hidden="cancelPlanning"
    >
      <b-tabs class="custom-tabs">
        <b-tab title="Запланированные работы" active>
          <b-card class="custom-card my-2">
            <b-form-group label="Время начала">
              <b-form-input
                v-model="workerItems.start_time"
                type="time"
              />
            </b-form-group>
          </b-card>
          <b-card class="custom-card">
            <div class="w-100 d-flex align-items-center justify-content-between">
              <b-form-group label="Дата окончания" class="w-100 mr-2">
                <b-form-input
                  v-model="workerItems.end_date"
                  type="date"
                />
              </b-form-group>
              <b-form-group label="Время окончания" class="w-100">
                <b-form-input
                  v-model="workerItems.end_time"
                  type="time"
                />
              </b-form-group>
            </div>
          </b-card>
          <b-card class="custom-card my-2">
            <div v-for="(work, index) in formattedLocationWorks" :key="index" class="mb-2">
              <b-row class="align-items-end">
                <b-col cols="9">
                  <b-form-group label="Вид работ">
                    <div class="position-relative">
                      <b-form-input
                          :value="work.work_type_name"
                          readonly
                      />
                      <div class="meta-info-overlay">
                        {{ work.rate }} {{ getUnitByCode(work.unit_code).shortName }}
                      </div>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col cols="3">
                  <b-form-group label="Кол-во">
                    <b-form-input
                      v-if="work.unit_code === '539'"
                      :value="calculatedHours"
                      disabled
                    />
                    <b-form-input
                      v-else
                      v-model.number="workerItems.total_units"
                      type="number"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <div v-if="formattedLocationWorks.length === 0" class="text-center text-muted p-2">
              Вид работ не выбран
            </div>
          </b-card>
        </b-tab>
        <b-tab title="Расходы">
          <b-card class="custom-card mt-2">
            <b-row>
              <b-col cols="6" class="mb-3">
                <b-form-group label="Ставка">
                  <b-form-input v-model.number="workerItems.price" type="number" step="0.5" min="0"/>
                </b-form-group>
              </b-col>
              <b-col cols="6" class="mb-3">
                <b-form-group label="Обед">
                  <b-form-input v-model.number="workerItems.dinner" type="number" step="0.5" min="0"/>
                </b-form-group>
              </b-col>
              <b-col cols="6" class="mb-3">
                <b-form-group label="Трансфер">
                  <b-form-input v-model.number="workerItems.road_price" type="number" min="0"/>
                </b-form-group>
              </b-col>
              <b-col cols="6" class="mb-3">
                <b-form-group label="Аванс">
                  <b-form-input v-model.number="workerItems.prepaid" type="number" min="0"/>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>
      </b-tabs>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <b-button class="custom-btn-secondary mr-2" size="sm" @click="isPlaningModalOpen = false">Отмена</b-button>
          <b-button variant="primary" class="custom-btn-primary" size="sm" @click="savePlaningData">Сохранить</b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import dateFormatter from '../../utilites/dateFormatter'
import unitStore from "../../utilites/unitStore";

const WEEK_DAYS =['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTH_NAMES =  ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

export default {
  name: 'TaskTimesheet',
  mixins: [ dateFormatter, unitStore ],
  props: {
    workers: { type: Array, required: true, default: () => [] },
    locationWorks: { type: Array, default: () => [] },
    initialSelected: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    scrollLocked: { type: Boolean, default: false }
  },
  data() {
    return {
      weekDays: WEEK_DAYS,
      monthNames: MONTH_NAMES,
      selectedCells: new Set(Array.isArray(this.initialSelected) ? this.initialSelected : []),
      planningMode: false,
      isFlashing: false,
      isBtnFlashing: false,
      isPlaningModalOpen: false,
      activePlanCells: [],
      hoveredDayIndex: null,
      selectedArea: null,
      drag: {
        active: false,
        startR: -1, startC: -1, curR: -1, curC: -1,
      },
      autoScroll: {
        active: false,
        mouseY: 0,
        frameId: null
      },
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      taskWorkerCells: {},
      workerItems: {},
      defaultWorkerItems: {
        start_time: '08:00',
        end_date: this.formatDate(new Date()),
        end_time: '20:00',
        price: 0.0,
        dinner: 0.0,
        road_price: 0.0,
        prepaid: 0.0,
        total_units: 0
      }
    }
  },
  mounted() {
    window.addEventListener('mouseup', this.onMouseUp);
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.onMouseUp);
  },
  computed: {
    formattedLocationWorks() {
      return this.locationWorks || [];
    },
    normalizedWorkers() {
      return this.workers.map(w => {
        const id = w.worker_id;
        const name = w.name;
        return { id, name };
      });
    },
    daysInCurrentMonth() {
      const days = [];
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();

      for (let i = 1; i <= lastDay; i++) {
        const dateObj = new Date(this.currentYear, this.currentMonth, i);
        const dayOfWeek = (dateObj.getDay() + 6) % 7;
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');

        days.push({
          dateObj,
          dateString: `${y}-${m}-${d}`,
          dayNumber: i,
          dayName: this.weekDays[dayOfWeek],
          isWeekend: dayOfWeek === 5 || dayOfWeek === 6
        });
      }
      return days;
    },
    availableYears() {
      const current = new Date().getFullYear();
      return [current, current + 1];
    },
    availableMonths() {
      const year = new Date().getFullYear();
      const month = new Date().getMonth();
      const months = [];

      for (let i = 0; i < 12; i++) {
        if (this.currentYear > year || i >= month - 1) {
          months.push({ index: i, name: this.monthNames[i] });
        }
      }
      return months;
    },
    calculatedHours() {
      if (!this.workerItems.start_time || !this.workerItems.end_time) return 0;

      const start = this.workerItems.start_time.split(':');
      const end = this.workerItems.end_time.split(':');
      const startDate = new Date(0, 0, 0, start[0], start[1], 0);
      const endDate = new Date(0, 0, 0, end[0], end[1], 0);

      let diff = endDate.getTime() - startDate.getTime();
      let hours = diff / 1000 / 60 / 60;
      const dinner = parseFloat(this.workerItems.dinner) || 0;
      const total = hours - dinner;
      return total > 0 ? total.toFixed(2) : 0;
    },
    activeSelectionBounds() {
      if (!this.drag.active && !this.selectedArea) return null;

      if (this.drag.active) {
        return {
          minR: Math.min(this.drag.startR, this.drag.curR),
          maxR: Math.max(this.drag.startR, this.drag.curR),
          minC: Math.min(this.drag.startC, this.drag.curC),
          maxC: Math.max(this.drag.startC, this.drag.curC)
        };
      }
      return this.selectedArea;
    }
  },
  watch: {
    currentYear() {
      if (!this.availableMonths.some(m => m.index === this.currentMonth)) {
        this.currentMonth = new Date().getMonth();
      }
    },
    initialSelected(newVal) {
      this.selectedCells = new Set(newVal || []);
    },
    workers(newVal) {
      const currentIds = newVal.map(w => w.worker_id);

      Object.keys(this.taskWorkerCells).forEach(key => {
        const workerId = parseInt(key.split('_')[0]);

        if (!currentIds.includes(workerId)) {
          this.selectedCells.delete(key);
          this.$delete(this.taskWorkerCells, key);
        }
      });
    },
    calculatedHours(newVal) {
      const isHours = this.locationWorks.some(w => w.unit_code === '539');
      if (isHours) {
        this.$set(this.workerItems, 'total_units', newVal);
      }
    }
  },
  methods: {
    trackMouse(e) {
      this.autoScroll.mouseY = e.clientY;
    },
    doAutoScroll() {
      if (!this.autoScroll.active) return;

      const container = this.$refs.scrollContainer;
      if (container) {
        const rect = container.getBoundingClientRect();
        const edgeSize = 60;
        const speed = 12;

        if (this.autoScroll.mouseY < rect.top + edgeSize) {
          container.scrollTop -= speed;
        }
        else if (this.autoScroll.mouseY > rect.bottom - edgeSize) {
          container.scrollTop += speed;
        }
      }

      this.autoScroll.frameId = requestAnimationFrame(this.doAutoScroll);
    },
    getDefaultCellData(dateString) {
      return {
        ...this.defaultWorkerItems,
        end_date: dateString
      };
    },
    getCellKey(workerId, dateString) {
      return `${workerId}_${dateString}`;
    },
    isSelected(workerId, dateString) {
      return this.selectedCells.has(this.getCellKey(workerId, dateString));
    },
    onMouseDown(wIndex, dIndex) {
      if (this.readonly) return;

      this.selectedArea = null;
      this.drag.active = true;
      this.drag.startR = this.drag.curR = wIndex;
      this.drag.startC = this.drag.curC = dIndex;

      window.addEventListener('mousemove', this.trackMouse);
      this.autoScroll.active = true;
      this.doAutoScroll();
    },
    onMouseEnter(wIndex, dIndex) {
      this.hoveredDayIndex = dIndex;

      if (this.drag.active) {
        this.drag.curR = wIndex;
        this.drag.curC = dIndex;
      }
    },
    onMouseUp() {
      window.removeEventListener('mousemove', this.trackMouse);
      this.autoScroll.active = false;

      if (this.autoScroll.frameId) {
        cancelAnimationFrame(this.autoScroll.frameId);
      }

      if (!this.drag.active) return;
      this.drag.active = false;

      const minR = Math.min(this.drag.startR, this.drag.curR);
      const maxR = Math.max(this.drag.startR, this.drag.curR);
      const minC = Math.min(this.drag.startC, this.drag.curC);
      const maxC = Math.max(this.drag.startC, this.drag.curC);

      const isSingleCell = minR === maxR && minC === maxC;

      if (isSingleCell) {
        const w = this.normalizedWorkers[minR];
        const d = this.daysInCurrentMonth[minC];
        const key = this.getCellKey(w.id, d.dateString);

        if (this.planningMode) {
          if (this.isSelected(w.id, d.dateString)) {
            this.activePlanCells = [{ workerId: w.id, date: d.dateString }];
            this.prepareModalData();
            this.isPlaningModalOpen = true;
          }
          return;
        }

        if (this.isSelected(w.id, d.dateString)) {
          this.selectedCells.delete(key);
          this.$delete(this.taskWorkerCells, key);
        } else {
          this.selectedCells.add(key);
          this.$set(this.taskWorkerCells, key, this.getDefaultCellData(d.dateString));
        }
        this.selectedCells = new Set(this.selectedCells);
        this.$emit('change', Array.from(this.selectedCells));
      } else {
        this.selectedArea = { minR, maxR, minC, maxC };
        this.planningMode ? this.processAreaForPlanning() : this.triggerBtnFlash();
      }
    },
    triggerBtnFlash() {
      this.isBtnFlashing = true;
      setTimeout(() => { this.isBtnFlashing = false; }, 1200);
    },
    toggleColumn(dateString) {
      if (this.readonly || this.planningMode) return;

      const allSelected = this.normalizedWorkers.every(w =>
        this.selectedCells.has(this.getCellKey(w.id, dateString))
      );

      this.normalizedWorkers.forEach(w => {
        const key = this.getCellKey(w.id, dateString);
        if (allSelected) {
          this.selectedCells.delete(key);
          this.$delete(this.taskWorkerCells, key);
        } else {
          this.selectedCells.add(key);
          if (!this.taskWorkerCells[key]) {
            this.$set(this.taskWorkerCells, key, this.getDefaultCellData(dateString));
          }
        }
      });

      this.selectedCells = new Set(this.selectedCells);
      this.$emit('change', Array.from(this.selectedCells));
    },
    processAreaForPlanning() {
      const targets = [];
      const { minR, maxR, minC, maxC } = this.selectedArea;

      for (let r = minR; r <= maxR; r++) {
        for (let c = minC; c <= maxC; c++) {
          const w = this.normalizedWorkers[r];
          const d = this.daysInCurrentMonth[c];
          if (this.isSelected(w.id, d.dateString)) {
            targets.push({ workerId: w.id, date: d.dateString });
          }
        }
      }

      if (targets.length === 0) {
        this.$bvToast.toast('В выделенной области нет назначенных исполнителей', {
          title: 'Внимание',
          variant: 'warning',
          autoHideDelay: 3000
        });
        return;
      }

      this.activePlanCells = targets;
      this.prepareModalData();
      this.isPlaningModalOpen = true;
    },
    prepareModalData() {
      if (this.activePlanCells.length === 0) return;

      let hasDifferences = false;
      const firstCellKey = this.getCellKey(this.activePlanCells[0].workerId, this.activePlanCells[0].date);
      const baseData = this.taskWorkerCells[firstCellKey] || this.getDefaultCellData(this.activePlanCells[0].date);

      const checkProps = Object.keys(this.defaultWorkerItems);

      for (let i = 1; i < this.activePlanCells.length; i++) {
        const key = this.getCellKey(this.activePlanCells[i].workerId, this.activePlanCells[i].date);
        const currentData = this.taskWorkerCells[key] || this.getDefaultCellData(this.activePlanCells[i].date);
        const isDifferent = checkProps.some(prop => baseData[prop] !== currentData[prop]);

        if (isDifferent) {
          hasDifferences = true;
          break;
        }
      }

      if (hasDifferences) {
        this.$bvToast.toast('У выбранной группы исполнителей отличаются параметры планирования, в окне будут отображаться стандартные значения', {
          title: 'Внимание',
          variant: 'warning',
          autoHideDelay: 5000
        });
        this.workerItems = { ...this.defaultWorkerItems };
      } else {
        this.workerItems = { ...baseData };
      }
    },
    startPlanning() {
      if (this.selectedArea) {
        this.processAreaForPlanning();
        return;
      }

      if (this.selectedCells.size === 0) {
        this.$bvToast.toast('Сначала назначьте исполнителей на даты', {
          title: 'Внимание',
          variant: 'warning',
          autoHideDelay: 3000
        });
        return;
      }

      this.planningMode = true;
      this.$bvToast.toast('Выберите область или отдельного исполнителя', {
        title: 'Планирование',
        variant: 'info',
        solid: true,
        autoHideDelay: 3000
      });

      this.isFlashing = true;
      setTimeout(() => { this.isFlashing = false; }, 600);
    },
    isCellInDragArea(r, c) {
      const bounds = this.activeSelectionBounds;

      if (!bounds) return false;

      return r >= bounds.minR && r <= bounds.maxR && c >= bounds.minC && c <= bounds.maxC;
    },
    savePlaningData() {
      const hasInvalidDates = this.activePlanCells.some(cell => new Date(this.workerItems.end_date) < new Date(cell.date));
      if (hasInvalidDates) {
        return this.$bvToast.toast('Дата окончания не может быть раньше начала!', { variant: 'danger' });
      }

      this.activePlanCells.forEach(cell => {
        const key = this.getCellKey(cell.workerId, cell.date);
        this.$set(this.taskWorkerCells, key, { ...this.workerItems });
      });

      this.cancelPlanning();
    },
    cancelPlanning() {
      this.isPlaningModalOpen = false;
      this.planningMode = false;
      this.selectedArea = null;
      this.activePlanCells = [];
    }
  }
}
</script>

<style scoped>
.timesheet-container {
  position: relative;
  overflow: auto;
  gap: 4px;
  width: 100%;
  height: 400px;
  background: #fff;
  user-select: none;
}

.timesheet-container.scroll-locked {
  overflow: hidden !important;
}

.timesheet-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: max-content;
  clip-path: inset(0);
}

.timesheet-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  width: max-content;
}

.timesheet-row:last-child {
  margin-bottom: 10px;
}

.timesheet-header {
  position: sticky;
  top: 0;
  background: #FFF;
  z-index: 10;
  padding-bottom: 4px;
}

.worker-col-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px;
  gap: 10px;
  width: 200px;
  height: 48px;
  font-size: 13px;
  background: #F4F5F5 !important;;
  border-radius: 6px;
  position: sticky;
  left: 0;
  top: 0;
  z-index: 10;
  will-change: transform;
}

.worker-col-name {
  font-size: 13px;
  width: 200px;
  position: sticky;
  left: 0;
  background: #FFF;
  z-index: 4;
  border-radius: 6px;
  align-self: stretch;
  line-height: 30px;
  box-shadow: 4px 0 0 #FFF, 0 4px 0 #FFF, 4px 4px 0 #FFF;
  will-change: transform;
}

.day-header-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  gap: 2px;
  width: 32px;
  height: 48px;
  background: rgba(10, 15, 23, 0.04);
  border-radius: 6px;
  flex: none;
  font-size: 11px;
  font-weight: 600;
  color: #333;
  transition: background-color 0.2s;
}

.day-header-cell.is-clickable {
  cursor: pointer;
}

.day-header-cell.is-clickable:hover {
  background: rgba(10, 15, 23, 0.1);
}

.day-header-cell.is-weekend {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.05);
}

.day-header-cell.is-clickable.is-weekend:hover {
  background: rgba(220, 53, 69, 0.15);
}

.day-cell {
  position: relative;
  width: 32px;
  height: 30px;
  background: rgba(10, 15, 23, 0.02);
  border-radius: 6px;
  flex: none;
  cursor: pointer;
  transition: background-color 0.1s;
}

.day-cell.is-readonly {
  cursor: default;
}

.day-cell.is-selected {
  background-color: #1A9167;
  border-color: #1A9167;
}

/* Ховеры и перекрестия */

.timesheet-row:hover .day-cell:not(.is-readonly):not(.is-selected),
.day-cell.is-col-hovered:not(.is-readonly):not(.is-selected) {
  background-color: rgba(10, 15, 23, 0.06);
}

.timesheet-row:hover .day-cell.is-col-hovered:not(.is-readonly):not(.is-selected) {
  background-color: rgba(10, 15, 23, 0.12);
}

.timesheet-row:hover .day-cell.is-selected:not(.is-readonly),
.day-cell.is-col-hovered.is-selected:not(.is-readonly) {
  background-color: #147251;
}

.timesheet-row:hover .day-cell.is-col-hovered.is-selected:not(.is-readonly) {
  background-color: #0f5c40;
}

/* Выделение */

.day-cell.is-dragged::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: inherit;
  pointer-events: none;
  z-index: 3;
}

.day-cell.is-selected.is-dragged::after {
  background: rgba(0, 0, 0, 0.3);
}

/* Анимация планирования */

@keyframes flashActiveCells {
  0%, 100% { background-color: #1A9167; box-shadow: none; }
  50% { background-color: #27d99b; box-shadow: 0 0 8px #27d99b; transform: scale(1.05); z-index: 5; }
}

.timesheet-container.is-flashing .day-cell.is-selected {
  animation: flashActiveCells 0.3s ease-in-out 2;
}

@keyframes flashButton {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.03); opacity: 0.8; }
}

button.custom-btn-secondary.is-btn-flashing {
  background-color: #1A9167 !important;
  color: #FFF !important;
  border-color: #1A9167 !important;
  animation: flashButton 0.4s ease-in-out 2;
}

.timesheet-container.is-planning .day-cell.is-disabled {
  opacity: 0.4;
}

.timesheet-container.is-planning .day-cell.is-selected {
  border: 1px solid #1A9167;
}

/* Прочее */
.meta-info-overlay {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.85rem;
  background-color: #f8f9fa;
  padding: 2px 8px;
  border-radius: 4px;
  color: #6c757d;
  border: 1px solid #dee2e6;
  pointer-events: none;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/Tasks/TaskTimesheet.vue